import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  Pencil,
  ArrowClockwise,
  CheckCircle,
  Prohibit,
  Star,
  Ticket,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { safeInsert, safeUpdate } from "@/lib/schema";

const EMPTY = {
  id: null,
  code: "",
  title: "",
  description: "",
  discount_type: "percent",
  discount_value: 10,
  min_order: 0,
  currency: "INR",
  starts_at: "",
  expires_at: "",
  max_uses: null,
  is_active: true,
  is_featured: false,
  show_on_site: true,
  badge: "",
  free_delivery: false,
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtValue = (c) =>
  c.discount_type === "flat"
    ? `${c.currency || "INR"} ${Number(c.discount_value || 0).toLocaleString("en-IN")}`
    : `${Number(c.discount_value || 0)}% OFF`;

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("admin-coupons")
      .on("postgres_changes", { event: "*", schema: "public", table: "coupons" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((c) => {
      const active = c.is_active !== false;
      if (statusFilter === "active" && !active) return false;
      if (statusFilter === "inactive" && active) return false;
      if (statusFilter === "featured" && !c.is_featured) return false;
      if (statusFilter === "hidden" && c.show_on_site !== false) return false;
      if (!query) return true;
      return [c.code, c.title, c.description, c.badge]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(query));
    });
  }, [rows, q, statusFilter]);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (c) => {
    setEditing({
      ...EMPTY,
      ...c,
      starts_at: c.starts_at ? String(c.starts_at).slice(0, 10) : "",
      expires_at: c.expires_at ? String(c.expires_at).slice(0, 10) : "",
    });
  };

  const save = async (payload) => {
    if (!payload.code || !payload.code.trim()) {
      toast.error("Coupon code is required");
      return;
    }
    setSaving(true);
    try {
      const full = {
        code: payload.code.trim().toUpperCase(),
        title: payload.title?.trim() || null,
        description: payload.description?.trim() || null,
        discount_type: payload.discount_type || "percent",
        discount_value: Number(payload.discount_value || 0),
        min_order: Number(payload.min_order || 0),
        currency: payload.currency || "INR",
        starts_at: payload.starts_at ? new Date(payload.starts_at).toISOString() : null,
        expires_at: payload.expires_at ? new Date(payload.expires_at).toISOString() : null,
        max_uses: payload.max_uses ? Number(payload.max_uses) : null,
        is_active: !!payload.is_active,
        is_featured: !!payload.is_featured,
        show_on_site: payload.show_on_site !== false,
        badge: payload.badge?.trim() || null,
        free_delivery: !!payload.free_delivery,
      };
      const res = payload.id
        ? await safeUpdate("coupons", full, { id: payload.id })
        : await safeInsert("coupons", full);
      if (res.error) throw res.error;
      toast.success(payload.id ? "Coupon updated" : "Coupon created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    const next = !(c.is_active !== false);
    const { error } = await supabase.from("coupons").update({ is_active: next }).eq("id", c.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "Activated" : "Deactivated");
      load();
    }
  };

  const toggleShow = async (c) => {
    const next = !(c.show_on_site !== false);
    const { error } = await supabase.from("coupons").update({ show_on_site: next }).eq("id", c.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "Now visible on site" : "Hidden from site");
      load();
    }
  };

  const toggleFeatured = async (c) => {
    const next = !c.is_featured;
    const { error } = await supabase.from("coupons").update({ is_featured: next }).eq("id", c.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "Featured" : "Unfeatured");
      load();
    }
  };

  const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();

  return (
    <AdminLayout title="Coupons">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code, title, description…"
            className="pl-9 h-11 rounded-full bg-white border-slate-200"
            data-testid="coupons-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px] h-11 rounded-full bg-white border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All coupons</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="hidden">Hidden from site</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={load}
          variant="outline"
          className="rounded-full h-11 border-slate-200"
          data-testid="coupons-refresh"
        >
          <ArrowClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
        <Button
          onClick={openNew}
          className="rounded-full h-11 bg-navy-deep hover:bg-navy text-white"
          data-testid="coupons-add"
        >
          <Plus size={14} weight="bold" className="mr-1.5" /> Add coupon
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Code</th>
                <th className="text-left px-5 py-3 font-semibold">Discount</th>
                <th className="text-left px-5 py-3 font-semibold">Min order</th>
                <th className="text-left px-5 py-3 font-semibold">Expires</th>
                <th className="text-left px-5 py-3 font-semibold">Uses</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-400">Loading coupons…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-400">No coupons yet. Click <span className="font-semibold">Add coupon</span> to create your first one.</td></tr>
              ) : (
                filtered.map((c) => {
                  const active = c.is_active !== false;
                  const visible = c.show_on_site !== false;
                  const expired = isExpired(c);
                  return (
                    <tr key={c.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-9 w-9 rounded-lg bg-teal-brand/10 grid place-items-center text-teal-brand">
                            <Ticket size={16} weight="bold" />
                          </div>
                          <div>
                            <div className="font-mono-data font-bold text-navy-deep tracking-wide text-[13px]">
                              {c.code}
                            </div>
                            {c.title && <div className="text-slate-500 text-[12px] max-w-[260px] truncate">{c.title}</div>}
                          </div>
                          {c.is_featured && <Star size={13} weight="fill" className="text-amber-400 ml-1" />}
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono-data text-navy-deep">{fmtValue(c)}</td>
                      <td className="px-5 py-3 text-slate-600">
                        {c.min_order > 0 ? `${c.currency || "INR"} ${Number(c.min_order).toLocaleString("en-IN")}` : "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-[12px]">
                        {c.expires_at ? (
                          <span className={expired ? "text-red-500" : ""}>{fmtDate(c.expires_at)}{expired && " (expired)"}</span>
                        ) : "Never"}
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-[12px]">
                        {c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                            {active ? "ACTIVE" : "INACTIVE"}
                          </span>
                          <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${visible ? "bg-cyan-50 text-cyan-700" : "bg-slate-100 text-slate-500"}`}>
                            {visible ? "ON SITE" : "HIDDEN"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => toggleFeatured(c)} className={`h-8 w-8 rounded-lg grid place-items-center ${c.is_featured ? "text-amber-500 hover:bg-amber-50" : "text-slate-500 hover:text-amber-500 hover:bg-slate-100"}`} title="Toggle featured">
                            <Star size={14} weight={c.is_featured ? "fill" : "regular"} />
                          </button>
                          <button onClick={() => toggleShow(c)} className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:text-cyan-brand hover:bg-slate-100" title={visible ? "Hide from site" : "Show on site"}>
                            {visible ? <Eye size={14} /> : <EyeSlash size={14} />}
                          </button>
                          <button onClick={() => toggleActive(c)} className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:text-cyan-brand hover:bg-slate-100" title={active ? "Deactivate" : "Activate"}>
                            {active ? <Prohibit size={14} /> : <CheckCircle size={14} />}
                          </button>
                          <button onClick={() => openEdit(c)} className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:text-cyan-brand hover:bg-slate-100" title="Edit">
                            <Pencil size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit coupon" : "New coupon"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={editing.code}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                    placeholder="AEONYX10"
                    className="font-mono-data tracking-widest uppercase"
                    data-testid="coupon-code"
                  />
                </div>
                <div>
                  <Label>Title (short label on card)</Label>
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Launch offer"
                    data-testid="coupon-title"
                  />
                </div>
              </div>

              <div>
                <Label>Description (shown under the code on the storefront)</Label>
                <Textarea
                  rows={2}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Get 10% off your first CryoVault. Use at checkout."
                  data-testid="coupon-description"
                />
              </div>

              <div className="rounded-2xl border border-teal-brand/30 bg-teal-brand/5 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.free_delivery}
                    onChange={(e) => setEditing({ ...editing, free_delivery: e.target.checked })}
                    className="mt-1 h-4 w-4 accent-cyan-brand"
                    data-testid="coupon-free-delivery"
                  />
                  <div>
                    <div className="text-sm font-semibold text-navy-deep">Free delivery</div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      When enabled, this coupon also unlocks free shipping at checkout and shows a
                      "FREE DELIVERY" badge on the storefront card.
                    </div>
                  </div>
                </label>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Discount type</Label>
                  <Select
                    value={editing.discount_type}
                    onValueChange={(v) => setEditing({ ...editing, discount_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                      <SelectItem value="flat">Flat amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input
                    type="number"
                    min={0}
                    step={editing.discount_type === "percent" ? 1 : 10}
                    value={editing.discount_value}
                    onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })}
                    data-testid="coupon-value"
                  />
                </div>
                <div>
                  <Label>Min order</Label>
                  <Input
                    type="number"
                    min={0}
                    step={100}
                    value={editing.min_order}
                    onChange={(e) => setEditing({ ...editing, min_order: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Starts on</Label>
                  <Input
                    type="date"
                    value={editing.starts_at}
                    onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Expires on</Label>
                  <Input
                    type="date"
                    value={editing.expires_at}
                    onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })}
                    data-testid="coupon-expires"
                  />
                </div>
                <div>
                  <Label>Max uses (blank = unlimited)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editing.max_uses ?? ""}
                    onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Badge (optional)</Label>
                  <Input
                    value={editing.badge}
                    onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                    placeholder="LIMITED"
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select
                    value={editing.currency}
                    onValueChange={(v) => setEditing({ ...editing, currency: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-5 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.is_active}
                    onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.show_on_site}
                    onChange={(e) => setEditing({ ...editing, show_on_site: e.target.checked })}
                    data-testid="coupon-show"
                  />
                  Show on storefront
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.is_featured}
                    onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })}
                  />
                  Featured (highlighted card)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full h-11 border-slate-200">Cancel</Button>
                <Button onClick={() => save(editing)} disabled={saving} className="rounded-full h-11 bg-navy-deep hover:bg-navy text-white" data-testid="coupon-save">
                  {saving ? "Saving…" : editing.id ? "Save changes" : "Create coupon"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
